import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, AlertTriangle, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [form, setForm] = useState({ new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) navigate('/auth');
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.new_password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: form.new_password });
      setSuccess(true);
      toast.success('Mot de passe réinitialisé !');
      setTimeout(() => navigate('/auth'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Nouveau mot de passe — Kurso</title>
      </Helmet>
      <div className="min-h-screen bg-[#0f0e17] flex flex-col items-center justify-center px-4 py-12">
        <div className="absolute top-20 left-1/4 w-64 h-64 bg-[#ff6b6b]/5 rounded-full blur-3xl pointer-events-none" />
        <button onClick={() => navigate('/')} className="font-heading font-bold text-2xl text-white mb-10">
          Kurso
        </button>

        <div className="w-full max-w-sm">
          <div className="kurso-card p-8 animate-fade-up">
            <div className="flex items-center justify-center w-12 h-12 bg-[#ff6b6b]/10 rounded-2xl mb-6 mx-auto">
              <BookOpen size={22} className="text-[#ff6b6b]" />
            </div>

            {success ? (
              <div className="text-center">
                <CheckCircle size={40} className="text-[#06d6a0] mx-auto mb-4" />
                <h1 className="font-heading text-xl font-bold text-white mb-2">Mot de passe modifié !</h1>
                <p className="text-[#a0a0ab] text-sm mb-4">Vous allez être redirigé vers la page de connexion...</p>
              </div>
            ) : (
              <>
                <h1 className="font-heading text-2xl font-bold text-white text-center mb-1">Nouveau mot de passe</h1>
                <p className="text-[#a0a0ab] text-sm text-center mb-6">Choisissez un nouveau mot de passe sécurisé.</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm text-[#e8e8e8] mb-1.5 block">Nouveau mot de passe</label>
                    <div className="relative">
                      <input
                        type={showPw ? 'text' : 'password'}
                        value={form.new_password}
                        onChange={e => setForm(p => ({ ...p, new_password: e.target.value }))}
                        placeholder="6 caractères minimum"
                        required minLength={6}
                        data-testid="reset-new-password-input"
                        className="w-full bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none focus:border-[#ff6b6b]/50 transition-colors"
                      />
                      <button type="button" onClick={() => setShowPw(!showPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a0a0ab] hover:text-white">
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-[#e8e8e8] mb-1.5 block">Confirmer le mot de passe</label>
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={form.confirm_password}
                      onChange={e => setForm(p => ({ ...p, confirm_password: e.target.value }))}
                      placeholder="Répétez le mot de passe"
                      required
                      data-testid="reset-confirm-password-input"
                      className="w-full bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#ff6b6b]/50 transition-colors"
                    />
                  </div>

                  {error && (
                    <div className="flex items-start gap-2 text-[#ff6b6b] text-sm bg-[#ff6b6b]/10 rounded-xl px-4 py-3 border border-[#ff6b6b]/20" data-testid="reset-error">
                      <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" /> {error}
                    </div>
                  )}

                  <button type="submit" disabled={loading}
                    data-testid="reset-submit-btn"
                    className="btn-primary w-full py-3.5 text-sm disabled:opacity-50">
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 rounded-full border-2 border-[#0f0e17] border-t-transparent animate-spin" />
                        Réinitialisation...
                      </span>
                    ) : 'Réinitialiser le mot de passe'}
                  </button>
                </form>

                <p className="text-center mt-4">
                  <button onClick={() => navigate('/auth')} className="text-sm text-[#a0a0ab] hover:text-[#ff6b6b] transition-colors">
                    ← Retour à la connexion
                  </button>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
